import React, { useCallback, useState } from "react";
import "../Quiz/Quiz.css";
import "./DropGame.css";
import "./MunchGame.css";
import { Seo } from "../../Shared/Seo/Seo.tsx";
import { IntroLead, IntroRest } from "../../Landing/IntroParagraphs.tsx";
import { findBySlug } from "../../Shared/Seo/routeSeo.ts";
import { Operations } from "../../MathFactsGenerator/Constants.ts";
import { AnswerResult } from "../Quiz/answer.ts";
import { munchBestScoreKey, readBestScore, recordScore } from "../Quiz/bestScore.ts";
import { factKey, recordCorrect, recordMiss } from "../Quiz/missedFacts.ts";
import { MissedFactsPractice } from "../Quiz/MissedFactsPractice.tsx";
import { track } from "../../Shared/analytics.ts";
import { getRandomInt } from "../../Shared/Helpers.tsx";
import { flashCardSettings } from "../deck.ts";
import { explain, MunchGame, MunchResult, ruleText, squareText } from "./MunchGame.tsx";
import { munchGameEventParams } from "./gameEventParams.ts";
import { FactPicker, operationsOf } from "./FactPicker.tsx";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";
import { format } from "../../Shared/i18n/format.ts";

interface MunchGamePageProps {
    slug: string;
    operation: Operations;
}

interface Summary {
    result: MunchResult;
    best: number | null;
    isNewBest: boolean;
}

const pickIndex = (n: number) => getRandomInt(0, n - 1);

/**
 * Math Munch (ADR 0056) in its keyword landing page: choose the facts and
 * monsters or calm, play, then see every wrong munch explained. A quiz route
 * carrying `game: "munch"`; its `quiz` is only the operation the picker opens
 * on (ADR 0098 §4).
 */
export function MunchGamePage({ slug, operation }: MunchGamePageProps) {
    const entry = findBySlug(slug)!;
    const t = useTranslation();
    // Only the picker's buttons use these: the board enumerates fact families
    // rather than drawing from the generator, so there are no digit ranges.
    const [settings, setSettings] = useState(() => flashCardSettings(operation));
    // Captured at the start, so a result is keyed by what was actually played.
    const [playedOperations, setPlayedOperations] = useState<string[]>([]);
    const [calm, setCalm] = useState<boolean | null>(null);
    const [runId, setRunId] = useState(0);
    const [summary, setSummary] = useState<Summary | null>(null);
    const [missesVersion, setMissesVersion] = useState(0);

    function start(chosenCalm: boolean) {
        setPlayedOperations(operationsOf(settings));
        setCalm(chosenCalm);
        setSummary(null);
        setRunId((id) => id + 1);
    }

    const handleFact = useCallback((front: string, result: AnswerResult) => {
        if (result === "correct") {
            recordCorrect(factKey(front));
        } else {
            recordMiss(factKey(front));
        }
        setMissesVersion((v) => v + 1);
    }, []);

    const handleFinish = useCallback(
        (result: MunchResult) => {
            const key = munchBestScoreKey(playedOperations, result.calm);
            const previous = readBestScore(key);
            const best = recordScore(key, result.munched);
            // After the store, as on the quiz: analytics never costs a best score.
            track("quiz_completed", munchGameEventParams(playedOperations, result));
            setSummary({
                result,
                best: best > 0 ? best : null,
                isNewBest: previous !== null ? result.munched > previous : result.munched > 0,
            });
        },
        [playedOperations]
    );

    const playing = calm !== null && summary === null;

    return (
        <>
            <Seo entry={entry} />
            <div className="landingContent">
                <h1>{entry.h1}</h1>
                <IntroLead paragraphs={entry.intro} />
            </div>

            <div className="quizTool munchTool">
                {!playing && !summary && <p className="munchHowTo">{t("flashCards.munchHowTo")}</p>}

                {calm === null ? (
                    <div className="quizModePicker">
                        <p className="quizModePrompt">{t("flashCards.munchWhichFacts")}</p>
                        <FactPicker settings={settings} setSettings={setSettings} label={t("flashCards.munchWhichFacts")} />
                        <p className="quizModePrompt">{t("flashCards.munchModePrompt")}</p>
                        <div className="quizModeButtons">
                            <button type="button" onClick={() => start(false)}>
                                {t("flashCards.munchWithMonsters")}
                            </button>
                            <button type="button" onClick={() => start(true)}>
                                {t("flashCards.munchCalm")}
                            </button>
                        </div>
                    </div>
                ) : summary ? (
                    <div className="quizSummary">
                        <p className="quizSummaryCounts">{t("flashCards.gameOver")}</p>
                        <p className="quizSummaryScore">{summary.result.munched}</p>
                        <p className="quizSummaryScoreLabel">{t("flashCards.munchScoreCaption")}</p>
                        <p className="quizSummaryCounts">
                            {format(t("flashCards.munchReachedTemplate"), {
                                level: summary.result.level,
                                wrong: summary.result.wrong,
                            })}
                        </p>
                        {summary.best !== null && (
                            <p className={`quizSummaryBest${summary.isNewBest ? " quizSummaryBest--new" : ""}`}>
                                {summary.isNewBest
                                    ? t("flashCards.newBest")
                                    : format(t("flashCards.yourBestTemplate"), { n: summary.best })}
                            </p>
                        )}
                        {summary.result.mistakes.length > 0 && (
                            <div className="quizSummaryMisses">
                                <h3>{t("flashCards.worthAnotherLookHeading")}</h3>
                                <ul>
                                    {summary.result.mistakes.map((m, i) => (
                                        <li key={i}>
                                            <span className="quizSummaryMissProblem">
                                                {ruleText(m.rule, t)}: {squareText(m.square)}
                                            </span>
                                            <span className="quizSummaryMissYours">{explain(m.rule, m.square, t)}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        <button type="button" className="quizPlayAgain" onClick={() => start(calm)}>
                            {t("flashCards.playAgain")}
                        </button>
                        <button type="button" className="quizChangeMode" onClick={() => setCalm(null)}>
                            {t("flashCards.munchChangeMode")}
                        </button>
                    </div>
                ) : (
                    <MunchGame
                        key={runId}
                        calm={calm}
                        operations={playedOperations}
                        pick={pickIndex}
                        onFactMunched={handleFact}
                        onFinish={handleFinish}
                    />
                )}

                {!playing && <MissedFactsPractice refreshKey={missesVersion} />}
            </div>

            <IntroRest paragraphs={entry.intro} />

            {entry.faq && entry.faq.length > 0 && (
                <section className="landingFaq">
                    <h2>{t("flashCards.frequentlyAskedQuestions")}</h2>
                    {entry.faq.map((item, i) => (
                        <div key={i} className="faqItem">
                            <h3>{item.q}</h3>
                            <p>{item.a}</p>
                        </div>
                    ))}
                </section>
            )}
        </>
    );
}

export default MunchGamePage;
